/**
 * Finance — Advanced: Fixed Assets, Bank Reconciliation, FX Revaluation, Consolidation.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import AdvancedTable from "@/components/table/AdvancedTable";
import { Server, Landmark, RefreshCw, Building2, Plus, Upload } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { apiErrorMessage } from "@/lib/apiError";

const fmt = (n) => {
  try { return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0)); }
  catch { return `₹${Number(n || 0).toFixed(2)}`; }
};

export default function FinanceAssets() {
  const { activeWorkspaceId } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "fixed-assets");
  useEffect(() => { setSp((p) => { const n = new URLSearchParams(p); n.set("tab", tab); return n; }, { replace: true }); }, [tab, setSp]);
  return (
    <div className="space-y-6" data-testid="finance-assets-page">
      <PageHeader title="Advanced Finance" subtitle="Fixed assets, bank reconciliation, FX revaluation, multi-entity consolidation." icon={Server} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none flex-wrap h-auto bg-transparent p-0 border-b border-border w-full justify-start gap-1">
          <TabsTrigger value="fixed-assets" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="fa-tab-assets"><Server className="w-4 h-4 mr-2" />Fixed Assets</TabsTrigger>
          <TabsTrigger value="bank-recon" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="fa-tab-bank"><Landmark className="w-4 h-4 mr-2" />Bank Recon</TabsTrigger>
          <TabsTrigger value="fx" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="fa-tab-fx"><RefreshCw className="w-4 h-4 mr-2" />FX Reval</TabsTrigger>
          <TabsTrigger value="consolidation" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="fa-tab-cons"><Building2 className="w-4 h-4 mr-2" />Consolidation</TabsTrigger>
        </TabsList>
        <TabsContent value="fixed-assets" className="mt-6"><FixedAssetsTab wsId={activeWorkspaceId} /></TabsContent>
        <TabsContent value="bank-recon" className="mt-6"><BankReconTab wsId={activeWorkspaceId} /></TabsContent>
        <TabsContent value="fx" className="mt-6"><FxTab wsId={activeWorkspaceId} /></TabsContent>
        <TabsContent value="consolidation" className="mt-6"><ConsolidationTab wsId={activeWorkspaceId} /></TabsContent>
      </Tabs>
    </div>
  );
}

function FixedAssetsTab({ wsId }) {
  const [assets, setAssets] = useState([]);
  const [accts, setAccts] = useState([]);
  const [cats, setCats] = useState([]);
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(false);
  const [runOpen, setRunOpen] = useState(false);
  const empty = { name: "", category: "computers.desktop_laptop", acquisition_date: new Date().toISOString().slice(0, 10), acquisition_cost: 0, useful_life_years: null, method: "SLM", wdv_rate_pct: null, salvage_value: 0, asset_account_id: "", depreciation_expense_account_id: "", accumulated_depreciation_account_id: "" };
  const [form, setForm] = useState(empty);
  const [runForm, setRunForm] = useState({ period: new Date().toISOString().slice(0, 7) });

  const load = () => {
    api.get(`/finance/workspaces/${wsId}/fixed-assets`).then((r) => setAssets(r.data.assets || []));
    api.get(`/finance/workspaces/${wsId}/fixed-assets/depreciation-runs`).then((r) => setRuns(r.data.runs || []));
  };
  useEffect(() => {
    if (!wsId) return;
    load();
    api.get(`/finance/workspaces/${wsId}/accounts`).then((r) => setAccts(r.data.accounts || []));
    api.get(`/finance/workspaces/${wsId}/fixed-assets/categories`).then((r) => setCats(r.data.categories || []));
  }, [wsId]);

  const fixedAcct = accts.filter((a) => a.subtype === "fixed_asset");
  const depExpAcct = accts.filter((a) => a.subtype === "depreciation" || a.type === "expense");

  const create = async () => {
    try {
      const p = { ...form, acquisition_cost: Number(form.acquisition_cost), salvage_value: Number(form.salvage_value), useful_life_years: form.useful_life_years ? Number(form.useful_life_years) : null, wdv_rate_pct: form.wdv_rate_pct ? Number(form.wdv_rate_pct) : null };
      await api.post(`/finance/workspaces/${wsId}/fixed-assets`, p);
      toast.success("Asset added"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  const runDepreciation = async () => {
    try {
      await api.post(`/finance/workspaces/${wsId}/fixed-assets/depreciation-runs`, runForm);
      toast.success("Depreciation posted"); setRunOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const voidRun = async (r) => {
    const reason = window.prompt(`Void depreciation run for ${r.period}?\n\nThis reverses the journal entry and restores each asset's book value.\n\nReason (required):`);
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/finance/depreciation-runs/${r.id}/void`, null, { params: { reason: reason.trim() } });
      toast.success(`Depreciation for ${r.period} voided`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Void failed")); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{assets.length} active assets · {runs.length} depreciation runs</div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setRunOpen(true)} data-testid="fa-run-dep-btn"><RefreshCw className="w-4 h-4 mr-2" />Run depreciation</Button>
          <Button onClick={() => setOpen(true)} data-testid="fa-add-btn"><Plus className="w-4 h-4 mr-2" />New asset</Button>
        </div>
      </div>
      <Card><CardContent className="p-0">
        <AdvancedTable
          tableKey="fixed-assets"
          title="Fixed assets"
          rows={assets}
          columns={[
            { key: "name", label: "Name", width: 220, sortable: true, filterType: "text", frozen: true },
            { key: "category", label: "Category", width: 140, sortable: true, groupable: true, filterType: "text",
              render: (a) => <span className="text-xs">{a.category}</span> },
            { key: "acquisition_date", label: "Acquired", width: 130, sortable: true, filterType: "date-range" },
            { key: "method", label: "Method", width: 140, sortable: true, groupable: true,
              render: (a) => <Badge variant="outline">{a.method}{a.method === "WDV" && a.wdv_rate_pct ? ` @ ${a.wdv_rate_pct}%` : ""}</Badge> },
            { key: "acquisition_cost", label: "Cost", width: 140, sortable: true, align: "right", type: "currency", sum: true, filterType: "number-range",
              accessor: (a) => Number(a.acquisition_cost || 0),
              render: (a) => <span className="font-mono">{fmt(a.acquisition_cost)}</span> },
            { key: "accumulated_depreciation", label: "Accum. Dep.", width: 140, sortable: true, align: "right", type: "currency", sum: true,
              accessor: (a) => Number(a.accumulated_depreciation || 0),
              render: (a) => <span className="font-mono text-muted-foreground">{fmt(a.accumulated_depreciation)}</span> },
            { key: "book_value", label: "Book value", width: 140, sortable: true, align: "right", type: "currency", sum: true,
              accessor: (a) => Number(a.book_value || 0),
              render: (a) => <span className="font-mono">{fmt(a.book_value)}</span> },
          ]}
          emptyTitle="No fixed assets yet"
          emptyDescription="Register assets to depreciate them monthly with balanced JEs."
        />
      </CardContent></Card>

      {runs.length > 0 && (
        <Card><CardContent className="p-0">
          <h3 className="font-semibold p-3 border-b">Recent depreciation runs</h3>
          <table className="w-full text-sm">
            <thead className="bg-muted/40"><tr className="text-left"><th className="p-3">Period</th><th className="p-3 text-right">Assets</th><th className="p-3 text-right">Depreciation</th><th className="p-3">Status</th><th className="p-3 text-right">Action</th></tr></thead>
            <tbody>{runs.slice(0, 6).map((r) => (
              <tr key={r.id} className="border-t" data-testid={`fa-run-${r.id}`}>
                <td className="p-3 font-mono">{r.period}</td>
                <td className="p-3 text-right">{r.asset_count}</td>
                <td className="p-3 text-right font-mono">{fmt(r.total_depreciation)}</td>
                <td className="p-3"><Badge variant={r.status === "voided" ? "destructive" : "outline"}>{r.status}</Badge></td>
                <td className="p-3 text-right">
                  {r.status !== "voided" ? (
                    <Button size="sm" variant="outline" onClick={() => voidRun(r)} data-testid={`fa-run-void-${r.id}`}>
                      Void
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">voided</span>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </CardContent></Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="fa-create-dialog">
          <DialogHeader><DialogTitle>New fixed asset</DialogTitle><DialogDescription>Companies Act 2013, Schedule II useful lives auto-applied by category. Choose SLM (Straight Line) or WDV (Written Down Value).</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fa-name-input" /></div>
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="fa-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.key} ({c.useful_life_years}y)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Acquired</Label><Input type="date" value={form.acquisition_date} onChange={(e) => setForm({ ...form, acquisition_date: e.target.value })} data-testid="fa-date-input" /></div>
            <div><Label>Cost</Label><Input type="number" step="0.01" value={form.acquisition_cost} onChange={(e) => setForm({ ...form, acquisition_cost: e.target.value })} data-testid="fa-cost-input" /></div>
            <div><Label>Salvage</Label><Input type="number" step="0.01" value={form.salvage_value} onChange={(e) => setForm({ ...form, salvage_value: e.target.value })} data-testid="fa-salvage-input" /></div>
            <div><Label>Useful life (yrs, optional)</Label><Input type="number" value={form.useful_life_years || ""} onChange={(e) => setForm({ ...form, useful_life_years: e.target.value })} data-testid="fa-life-input" placeholder="auto-derived" /></div>
            <div><Label>Method</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="fa-method-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="SLM">SLM (Straight Line)</SelectItem><SelectItem value="WDV">WDV (Written Down Value)</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2 border-t pt-3">
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Fixed Asset a/c</Label>
                  <Select value={form.asset_account_id} onValueChange={(v) => setForm({ ...form, asset_account_id: v })}>
                    <SelectTrigger data-testid="fa-asset-acct-select"><SelectValue placeholder="…" /></SelectTrigger>
                    <SelectContent>{fixedAcct.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Dep. expense a/c</Label>
                  <Select value={form.depreciation_expense_account_id} onValueChange={(v) => setForm({ ...form, depreciation_expense_account_id: v })}>
                    <SelectTrigger data-testid="fa-dep-acct-select"><SelectValue placeholder="…" /></SelectTrigger>
                    <SelectContent>{depExpAcct.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Accum. dep. a/c</Label>
                  <Select value={form.accumulated_depreciation_account_id} onValueChange={(v) => setForm({ ...form, accumulated_depreciation_account_id: v })}>
                    <SelectTrigger data-testid="fa-accum-acct-select"><SelectValue placeholder="…" /></SelectTrigger>
                    <SelectContent>{fixedAcct.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={create} disabled={!form.name || !form.acquisition_cost || !form.asset_account_id || !form.depreciation_expense_account_id || !form.accumulated_depreciation_account_id} data-testid="fa-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={runOpen} onOpenChange={setRunOpen}>
        <DialogContent data-testid="fa-run-dep-dialog">
          <DialogHeader><DialogTitle>Run monthly depreciation</DialogTitle><DialogDescription>Computes SLM/WDV depreciation for every active asset and posts a batch journal.</DialogDescription></DialogHeader>
          <div><Label>Period (YYYY-MM)</Label><Input value={runForm.period} onChange={(e) => setRunForm({ period: e.target.value })} data-testid="fa-dep-period-input" /></div>
          <DialogFooter><Button onClick={runDepreciation} disabled={!runForm.period} data-testid="fa-dep-submit">Post depreciation</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BankReconTab({ wsId }) {
  const [accts, setAccts] = useState([]);
  const [bankId, setBankId] = useState("");
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Feb 2026 R287 · Preview-before-commit + Create-JE state.
  const [preview, setPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [jeFor, setJeFor] = useState(null);       // staged line pending JE
  const [contraId, setContraId] = useState("");
  const [jeSaving, setJeSaving] = useState(false);
  const [allAccts, setAllAccts] = useState([]);   // for contra picker
  useEffect(() => {
    if (!wsId) return;
    api.get(`/finance/workspaces/${wsId}/accounts`).then((r) => {
      const list = (r.data.accounts || []);
      setAllAccts(list);
      const banks = list.filter((a) => ["bank", "cash"].includes(a.subtype));
      setAccts(banks);
      if (banks.length && !bankId) setBankId(banks[0].id);
    });
  }, [wsId]);
  const load = () => {
    if (!bankId) return;
    api.get(`/finance/workspaces/${wsId}/bank-reconciliation?bank_account_id=${bankId}&period=${period}`).then((r) => setData(r.data));
  };
  useEffect(() => { if (bankId && period) load(); }, [bankId, period]);

  // Feb 2026 R287 · Two-step upload: preview first, then user confirms
  // commit. Previously the CSV wrote to `bank_statement_lines`
  // immediately with no way to catch a malformed file.
  const onFilePick = async (file) => {
    if (!file) return;
    setPendingFile(file); setPreview(null);
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/finance/workspaces/${wsId}/bank-statements/preview`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); setPendingFile(null); }
    finally { setUploading(false); }
  };

  const commitUpload = async () => {
    if (!pendingFile) return;
    setCommitting(true);
    const fd = new FormData();
    fd.append("bank_account_id", bankId);
    fd.append("period", period);
    fd.append("file", pendingFile);
    try {
      const { data } = await api.post(`/finance/workspaces/${wsId}/bank-statements`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Ingested ${data.lines_ingested} lines · match them or click "Create JE" to book each to the ledger.`);
      setPreview(null); setPendingFile(null);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setCommitting(false); }
  };
  const autoMatch = async () => {
    try {
      const { data } = await api.post(`/finance/workspaces/${wsId}/bank-reconciliation/auto-match?bank_account_id=${bankId}&period=${period}`);
      toast.success(`${data.matched} lines matched`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  const manualMatch = async (s, L) => {
    try {
      await api.post(`/finance/workspaces/${wsId}/bank-reconciliation/match`, { statement_line_id: s.id, journal_line_id: L.id });
      toast.success("Matched");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  const createJe = async () => {
    if (!jeFor || !contraId) { toast.error("Pick a contra account"); return; }
    setJeSaving(true);
    try {
      const { data } = await api.post(
        `/finance/workspaces/${wsId}/bank-reconciliation/create-je`,
        { statement_line_id: jeFor.id, contra_account_id: contraId });
      toast.success(`JE posted · amount ₹${fmt(data.amount)} (${data.direction === "in" ? "money in" : "money out"})`);
      setJeFor(null); setContraId("");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Could not post JE")); }
    finally { setJeSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="w-64"><Label>Bank account</Label>
          <Select value={bankId} onValueChange={setBankId}>
            <SelectTrigger data-testid="br-bank-select"><SelectValue /></SelectTrigger>
            <SelectContent>{accts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Period (YYYY-MM)</Label><Input value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="br-period-input" /></div>
        <div className="flex items-end gap-2">
          <Label className="cursor-pointer inline-flex items-center px-3 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90" data-testid="br-upload-btn">
            <Upload className="w-4 h-4 mr-2" />{uploading ? "Parsing…" : "Upload CSV"}
            <input type="file" accept=".csv" className="hidden" onChange={(e) => { onFilePick(e.target.files?.[0]); e.target.value = ""; }} data-testid="br-upload-input" />
          </Label>
          <Button variant="outline" onClick={autoMatch} disabled={!data} data-testid="br-automatch-btn">Auto-match</Button>
        </div>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-4 gap-2 text-sm" data-testid="br-stats">
            <div className="p-3 rounded border"><div className="text-xs text-muted-foreground">Statement lines</div><div className="text-lg font-mono">{data.stats.statement_count}</div></div>
            <div className="p-3 rounded border"><div className="text-xs text-muted-foreground">Statement matched</div><div className="text-lg font-mono">{data.stats.statement_matched}</div></div>
            <div className="p-3 rounded border"><div className="text-xs text-muted-foreground">Ledger lines</div><div className="text-lg font-mono">{data.stats.ledger_count}</div></div>
            <div className="p-3 rounded border"><div className="text-xs text-muted-foreground">Ledger matched</div><div className="text-lg font-mono">{data.stats.ledger_matched}</div></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card><CardContent className="p-6"><h3 className="font-semibold mb-3">Bank statement</h3>
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs"><tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Description</th><th className="p-2 text-right">Debit</th><th className="p-2 text-right">Credit</th><th className="p-2">Status</th></tr></thead>
                <tbody>{(data.statement_lines || []).map((s) => (
                  <tr key={s.id} className="border-t" data-testid={`br-stmt-${s.id}`}>
                    <td className="p-2 text-xs">{s.txn_date}</td>
                    <td className="p-2 text-xs">{s.description}</td>
                    <td className="p-2 text-right font-mono">{s.debit ? fmt(s.debit) : "—"}</td>
                    <td className="p-2 text-right font-mono">{s.credit ? fmt(s.credit) : "—"}</td>
                    <td className="p-2 text-xs">
                      {s.reconciled ? <Badge variant="outline" className="text-emerald-600">Matched</Badge> : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[10px]"
                          onClick={() => setJeFor(s)}
                          data-testid={`br-create-je-${s.id}`}
                          title="Create a journal entry from this staged line and move ledger numbers"
                        >Create JE</Button>
                      )}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </CardContent></Card>
            <Card><CardContent className="p-6"><h3 className="font-semibold mb-3">Ledger</h3>
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs"><tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Memo</th><th className="p-2 text-right">Debit</th><th className="p-2 text-right">Credit</th><th className="p-2">Status</th></tr></thead>
                <tbody>{(data.ledger_lines || []).map((L) => (
                  <tr key={L.id} className="border-t" data-testid={`br-led-${L.id}`}>
                    <td className="p-2 text-xs">{L.date}</td>
                    <td className="p-2 text-xs">{L.memo}</td>
                    <td className="p-2 text-right font-mono">{L.debit ? fmt(L.debit) : "—"}</td>
                    <td className="p-2 text-right font-mono">{L.credit ? fmt(L.credit) : "—"}</td>
                    <td className="p-2 text-xs">{L.reconciled ? <Badge variant="outline" className="text-emerald-600">Matched</Badge> : "—"}</td>
                  </tr>
                ))}</tbody>
              </table>
            </CardContent></Card>
          </div>
        </>
      )}

      {/* Feb 2026 R287 · Preview modal — parsed rows before commit. */}
      {preview && (
        <Dialog open onOpenChange={(v) => { if (!v) { setPreview(null); setPendingFile(null); } }}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="br-preview-dialog">
            <DialogHeader><DialogTitle>Preview bank statement upload</DialogTitle>
              <DialogDescription>
                {preview.line_count} row(s) parsed · <b>Debit</b> ₹{fmt(preview.totals?.debit || 0)} · <b>Credit</b> ₹{fmt(preview.totals?.credit || 0)} · Net {preview.totals?.net_credit >= 0 ? "in" : "out"} ₹{fmt(Math.abs(preview.totals?.net_credit || 0))} · Dates {preview.date_range?.from || "—"} → {preview.date_range?.to || "—"}
              </DialogDescription>
            </DialogHeader>
            <div className="border border-border max-h-72 overflow-y-auto text-xs">
              <table className="w-full">
                <thead className="bg-muted/40 sticky top-0"><tr>
                  <th className="p-1 text-left">Date</th>
                  <th className="p-1 text-left">Description</th>
                  <th className="p-1 text-right">Debit</th>
                  <th className="p-1 text-right">Credit</th>
                  <th className="p-1 text-right">Balance</th>
                </tr></thead>
                <tbody>{(preview.lines || []).map((r, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-1 font-mono">{r.txn_date}</td>
                    <td className="p-1 truncate max-w-[200px]" title={r.description}>{r.description}</td>
                    <td className="p-1 text-right font-mono">{r.debit ? fmt(r.debit) : ""}</td>
                    <td className="p-1 text-right font-mono">{r.credit ? fmt(r.credit) : ""}</td>
                    <td className="p-1 text-right font-mono text-muted-foreground">{r.balance != null ? fmt(r.balance) : ""}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            {preview.truncated && <div className="text-[10px] text-amber-700">Only first 500 rows shown — all will be committed.</div>}
            <div className="text-[10px] text-muted-foreground">
              Rows land in a <b>staging table</b> for reconciliation — matching them (or clicking <b>Create JE</b>) is what moves ledger numbers.
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => { setPreview(null); setPendingFile(null); }}>Cancel</Button>
              <Button onClick={commitUpload} disabled={committing} data-testid="br-preview-commit">
                {committing ? "Ingesting…" : `Ingest ${preview.line_count} row${preview.line_count === 1 ? "" : "s"}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Feb 2026 R287 · Create JE from staged line — pick a contra a/c. */}
      {jeFor && (
        <Dialog open onOpenChange={(v) => { if (!v) { setJeFor(null); setContraId(""); } }}>
          <DialogContent className="max-w-md" data-testid="br-je-dialog">
            <DialogHeader><DialogTitle>Book this line to the ledger</DialogTitle>
              <DialogDescription>
                {jeFor.txn_date} · {jeFor.description} · {jeFor.credit > 0
                  ? <>Money <b>in</b> · ₹{fmt(jeFor.credit)}</>
                  : <>Money <b>out</b> · ₹{fmt(jeFor.debit)}</>}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Contra account</Label>
                <select
                  value={contraId}
                  onChange={(e) => setContraId(e.target.value)}
                  className="w-full h-9 border border-border bg-background rounded-none px-2 text-sm"
                  data-testid="br-je-contra"
                >
                  <option value="">— pick account —</option>
                  {(allAccts || []).map((a) => (
                    <option key={a.id} value={a.id}>{a.code} — {a.name} ({a.type})</option>
                  ))}
                </select>
                <div className="text-[10px] text-muted-foreground mt-1">
                  {jeFor.credit > 0
                    ? "Common contras for money-in: Sales, AR clearing, other income."
                    : "Common contras for money-out: expenses, AP clearing, bank charges."}
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => { setJeFor(null); setContraId(""); }}>Cancel</Button>
              <Button onClick={createJe} disabled={jeSaving || !contraId} data-testid="br-je-commit">
                {jeSaving ? "Posting…" : "Post JE"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function FxTab({ wsId }) {
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/finance/workspaces/${wsId}/fx-revaluation?as_of=${asOf}`);
      setResult(data);
      toast.success(data.posted ? `Posted FX revaluation (net ${fmt(data.net_fx_gain_loss)})` : "No revaluation needed");
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <Card><CardContent className="p-6 space-y-3">
        <div className="flex items-end gap-3">
          <div><Label>As of</Label><Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="fx-asof-input" /></div>
          <Button onClick={run} disabled={busy} data-testid="fx-run-btn">Revalue foreign-currency balances</Button>
        </div>
        <p className="text-xs text-muted-foreground">Applies current FX rates to open AR/AP in non-base currencies, posts a gain/loss journal to code 5910 (FX gain/loss).</p>
      </CardContent></Card>
      {result && (
        <Card><CardContent className="p-6" data-testid="fx-result">
          {!result.posted ? <p className="text-sm text-muted-foreground">{result.message}</p> : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="p-3 rounded border"><div className="text-xs text-muted-foreground">AR revaluation</div><div className={`text-lg font-mono ${result.ar_diff_base >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmt(result.ar_diff_base)}</div></div>
                <div className="p-3 rounded border"><div className="text-xs text-muted-foreground">AP revaluation</div><div className={`text-lg font-mono ${result.ap_diff_base <= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmt(result.ap_diff_base)}</div></div>
                <div className="p-3 rounded border"><div className="text-xs text-muted-foreground">Net FX gain/loss</div><div className={`text-lg font-mono ${result.net_fx_gain_loss <= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmt(-result.net_fx_gain_loss)}</div></div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs"><tr><th className="p-2 text-left">Type</th><th className="p-2">Doc</th><th className="p-2">Ccy</th><th className="p-2 text-right">Outstanding</th><th className="p-2 text-right">Booked</th><th className="p-2 text-right">Current</th><th className="p-2 text-right">Diff</th></tr></thead>
                <tbody>{result.breakdown.map((b, i) => (
                  <tr key={i} className="border-t"><td className="p-2">{b.type}</td><td className="p-2 text-xs">{b.doc_no || b.bill_no}</td><td className="p-2 text-xs">{b.currency}</td><td className="p-2 text-right font-mono">{Number(b.outstanding_native).toFixed(2)}</td><td className="p-2 text-right font-mono">{b.booked_rate}</td><td className="p-2 text-right font-mono">{b.current_rate}</td><td className={`p-2 text-right font-mono ${b.diff_base >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmt(b.diff_base)}</td></tr>
                ))}</tbody>
              </table>
            </>
          )}
        </CardContent></Card>
      )}
    </div>
  );
}

function ConsolidationTab({ wsId }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [selected, setSelected] = useState([]);
  const [start, setStart] = useState(new Date(new Date().getFullYear(), 3, 1).toISOString().slice(0, 10));
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/auth/me`).then((r) => {
      const ws = r.data.workspaces || [];
      // Always ensure the active workspace is selectable, even if /auth/me
      // returns an empty list (single-workspace users).
      let list = ws;
      if (wsId && !ws.some((w) => w.id === wsId)) {
        list = [{ id: wsId, name: r.data.workspace?.name || "This workspace" }, ...ws];
      }
      setWorkspaces(list);
      if (wsId && !selected.length) setSelected([wsId]);
    });
  }, [wsId, selected.length]);

  const toggle = (id) => setSelected((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  const run = async () => {
    if (!selected.length) return;
    try {
      const { data } = await api.get(`/finance/workspaces/${wsId}/consolidation/p-and-l?workspace_ids=${selected.join(",")}&start=${start}&end=${end}`);
      setResult(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  return (
    <div className="space-y-4">
      <Card><CardContent className="p-6 space-y-3">
        <h3 className="font-semibold">Select entities to consolidate</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">{workspaces.map((w) => (
          <label key={w.id} className="flex items-center gap-2 border rounded p-2 cursor-pointer hover:bg-muted/50" data-testid={`cons-ws-${w.id}`}>
            <input type="checkbox" checked={selected.includes(w.id)} onChange={() => toggle(w.id)} />
            <span className="text-sm">{w.name}</span>
          </label>
        ))}{workspaces.length === 0 && <p className="text-sm text-muted-foreground col-span-3">No workspaces available.</p>}</div>
        <div className="flex items-end gap-3 pt-2 border-t">
          <div><Label>From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="cons-start-input" /></div>
          <div><Label>To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="cons-end-input" /></div>
          <Button onClick={run} disabled={!selected.length} data-testid="cons-run-btn">Consolidate</Button>
        </div>
      </CardContent></Card>
      {result && (
        <Card><CardContent className="p-6 space-y-3" data-testid="cons-result">
          <h3 className="font-semibold">Consolidated P&L ({result.start} → {result.end})</h3>
          <table className="w-full text-sm border rounded">
            <thead className="bg-muted/40 text-xs"><tr><th className="p-2 text-left">Entity</th><th className="p-2 text-right">Revenue</th><th className="p-2 text-right">Expenses</th><th className="p-2 text-right">Net</th></tr></thead>
            <tbody>{result.entities.map((e) => (
              <tr key={e.workspace_id} className="border-t"><td className="p-2">{e.workspace_name}</td><td className="p-2 text-right font-mono">{fmt(e.revenue_total)}</td><td className="p-2 text-right font-mono">{fmt(e.expenses_total)}</td><td className="p-2 text-right font-mono">{fmt(e.net_profit)}</td></tr>
            ))}</tbody>
            <tfoot className="border-t bg-muted/30 font-semibold font-mono">
              <tr><td className="p-2">Consolidated</td><td className="p-2 text-right" data-testid="cons-rev-total">{fmt(result.consolidated.revenue_total)}</td><td className="p-2 text-right" data-testid="cons-exp-total">{fmt(result.consolidated.expenses_total)}</td><td className={`p-2 text-right ${result.consolidated.net_profit >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="cons-net">{fmt(result.consolidated.net_profit)}</td></tr>
            </tfoot>
          </table>
          {result.consolidated.margin_pct != null && <div className="text-sm">Consolidated margin: <span className="font-mono font-semibold">{result.consolidated.margin_pct}%</span></div>}
        </CardContent></Card>
      )}
    </div>
  );
}
